import { useQuery } from '@tanstack/react-query';
import { dashboardService } from '../../services/adminService';

// Query keys for React Query
export const dashboardQueryKeys = {
  all: ['admin', 'dashboard'] as const,
  overview: () => [...dashboardQueryKeys.all, 'overview'] as const,
  userAnalytics: (timeRange: number) => [...dashboardQueryKeys.all, 'user-analytics', timeRange] as const,
  orderAnalytics: (timeRange: number) => [...dashboardQueryKeys.all, 'order-analytics', timeRange] as const,
  financialReports: (timeRange: number) => [...dashboardQueryKeys.all, 'financial-reports', timeRange] as const,
  geographicAnalytics: () => [...dashboardQueryKeys.all, 'geographic-analytics'] as const,
  recentActivities: () => [...dashboardQueryKeys.all, 'recent-activities'] as const,
  recentOrders: (page: number, perPage: number) => [...dashboardQueryKeys.all, 'recent-orders', page, perPage] as const,
  topUsers: (timeRange: number, page: number, perPage: number) => [...dashboardQueryKeys.all, 'top-users', timeRange, page, perPage] as const,
};

// Dashboard Overview Hook
export const useDashboardOverview = () => {
  return useQuery({
    queryKey: dashboardQueryKeys.overview(),
    queryFn: dashboardService.getDashboardOverview,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 10 * 60 * 1000, // 10 minutes
  });
};

// User Analytics Hook
export const useUserAnalytics = (timeRange = 30) => {
  return useQuery({
    queryKey: dashboardQueryKeys.userAnalytics(timeRange),
    queryFn: () => dashboardService.getUserAnalytics(timeRange),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: timeRange > 0,
  });
};

// Order Analytics Hook
export const useOrderAnalytics = (timeRange = 30) => {
  return useQuery({
    queryKey: dashboardQueryKeys.orderAnalytics(timeRange),
    queryFn: () => dashboardService.getOrderAnalytics(timeRange),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: timeRange > 0,
  });
};

// Financial Reports Hook
export const useFinancialReports = (timeRange = 30) => {
  return useQuery({
    queryKey: dashboardQueryKeys.financialReports(timeRange),
    queryFn: () => dashboardService.getFinancialReports(timeRange),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: timeRange > 0,
  });
};

// Geographic Analytics Hook
export const useGeographicAnalytics = () => {
  return useQuery({
    queryKey: dashboardQueryKeys.geographicAnalytics(),
    queryFn: dashboardService.getGeographicAnalytics,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Recent Activities Hook
export const useRecentActivities = () => {
  return useQuery({
    queryKey: dashboardQueryKeys.recentActivities(),
    queryFn: dashboardService.getRecentActivities,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 5 * 60 * 1000, // 5 minutes
  });
};

// Recent Orders Hook
export const useRecentOrders = (page = 1, perPage = 15) => {
  return useQuery({
    queryKey: dashboardQueryKeys.recentOrders(page, perPage),
    queryFn: () => dashboardService.getRecentOrders(page, perPage),
    staleTime: 3 * 60 * 1000, // 3 minutes
    enabled: page > 0 && perPage > 0,
  });
};

// Top Users Hook
export const useTopUsers = (timeRange = 30, page = 1, perPage = 15) => {
  return useQuery({
    queryKey: dashboardQueryKeys.topUsers(timeRange, page, perPage),
    queryFn: () => dashboardService.getTopUsers(timeRange, page, perPage),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: timeRange > 0 && page > 0 && perPage > 0,
  });
};

// Combined dashboard data hook
export const useDashboardData = (timeRange = 30) => {
  const overview = useDashboardOverview();
  const userAnalytics = useUserAnalytics(timeRange);
  const orderAnalytics = useOrderAnalytics(timeRange);
  const financialReports = useFinancialReports(timeRange);
  const geographicAnalytics = useGeographicAnalytics();
  const recentActivities = useRecentActivities();
  const recentOrders = useRecentOrders(1, 5); // Get first 5 for dashboard

  return {
    overview,
    userAnalytics,
    orderAnalytics,
    financialReports,
    geographicAnalytics,
    recentActivities,
    recentOrders,
    isLoading: overview.isLoading || userAnalytics.isLoading || orderAnalytics.isLoading,
    isError: overview.isError || userAnalytics.isError || orderAnalytics.isError,
    refetchAll: () => {
      overview.refetch();
      userAnalytics.refetch();
      orderAnalytics.refetch();
      financialReports.refetch();
      geographicAnalytics.refetch();
      recentActivities.refetch();
      recentOrders.refetch();
    },
  };
}; 